import {
  B2C_REPOS,
  type B2cIterationBranchRow,
  type B2cIterationRow,
  type B2cRepo,
  type B2cStageRow,
  type B2cTaskRow,
  type B2cWorkstreamGateRow,
  type B2cWorkstreamRow,
} from "@/infrastructure/supabase/b2c-board.types";

/**
 * The B2C board's view model: stage → workstream → iteration → tasks, with the
 * marathon progress and the per-flow gates worked out once, outside the UI.
 *
 * Rules (from the 14 Oct marathon-model session):
 *  - A workstream runs one iteration at a time as a marathon; its "current"
 *    iteration is the running marathon, else the earliest one not merged yet,
 *    else the latest one.
 *  - A stage-2 workstream's gate opens once every workstream it builds on has
 *    merged at least one iteration.
 *  - Progress counts done tasks; deferred tasks leave the total.
 *  - An iteration may change several repos, each on its own integration branch
 *    (0144). Each repo's branch stacks on the previous iteration's branch in the
 *    same repo; the iteration is merged once every one of its branches has.
 */

export interface BranchView {
  branch: B2cIterationBranchRow;
  /** Cut from and PRs into: the previous iteration's branch in this repo while unmerged, else main. */
  base: string;
}

export interface Progress {
  done: number;
  total: number;
  /** 0..1; 0 when there is nothing to do */
  ratio: number;
}

export interface IterationView {
  iteration: B2cIterationRow;
  tasks: B2cTaskRow[];
  progress: Progress;
  /**
   * Stacked branches: while the previous iteration is not merged, this
   * iteration's integration branch is based on the previous one's branch, not
   * on main. Null when the previous iteration has merged (or there is none).
   */
  stackedOn: { number: number; branch: string } | null;
  /** The branch this iteration's integration branch is cut from and PRs into. */
  baseBranch: string;
  /** Merge bottom-up: the number of an earlier iteration still unmerged, if any. */
  mergeBlockedBy: number | null;
  /** One per repo the iteration changes, yango-app first. */
  branches: BranchView[];
  /** Repos whose branch has not merged yet; the iteration merges when this is empty. */
  openRepos: B2cRepo[];
}

export interface GateView {
  key: string;
  code: string;
  title: string;
  merged: boolean;
}

export interface WorkstreamView {
  workstream: B2cWorkstreamRow;
  iterations: IterationView[];
  /** Tasks with no iteration yet. */
  unplanned: B2cTaskRow[];
  current: IterationView | null;
  buildsOn: GateView[];
  gateOpen: boolean;
  hasMerged: boolean;
  progress: Progress;
  e2eTask: B2cTaskRow | null;
}

export interface StageView {
  stage: B2cStageRow;
  workstreams: WorkstreamView[];
  progress: Progress;
}

export interface B2cBoardView {
  stages: StageView[];
  /** B2C workstreams with no stage (shown after the stages). */
  unstaged: WorkstreamView[];
  summary: {
    workstreams: number;
    marathons: number;
    awaitingFeedback: number;
    progress: Progress;
  };
}

export function progressOf(tasks: B2cTaskRow[]): Progress {
  const counted = tasks.filter((t) => t.status !== "deferred");
  const done = counted.filter((t) => t.status === "done").length;
  const total = counted.length;
  return { done, total, ratio: total === 0 ? 0 : done / total };
}

function sumProgress(parts: Progress[]): Progress {
  const done = parts.reduce((n, p) => n + p.done, 0);
  const total = parts.reduce((n, p) => n + p.total, 0);
  return { done, total, ratio: total === 0 ? 0 : done / total };
}

export function currentIteration(iterations: IterationView[]): IterationView | null {
  return (
    iterations.find((i) => i.iteration.status === "marathon") ??
    iterations.find((i) => i.iteration.status !== "merged") ??
    iterations[iterations.length - 1] ??
    null
  );
}

export function nextIterationNumber(iterations: B2cIterationRow[]): number {
  return iterations.reduce((max, i) => Math.max(max, i.number), 0) + 1;
}

/** `integration/b2c-f-it2` for workstream F, iteration 2. */
export function defaultIntegrationBranch(workstreamCode: string, number: number): string {
  const slug = workstreamCode
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `integration/b2c-${slug}-it${number}`;
}

export function buildB2cBoard(input: {
  stages: B2cStageRow[];
  workstreams: B2cWorkstreamRow[];
  gates: B2cWorkstreamGateRow[];
  iterations: B2cIterationRow[];
  branches: B2cIterationBranchRow[];
  tasks: B2cTaskRow[];
}): B2cBoardView {
  const branchesByIteration = new Map<string, B2cIterationBranchRow[]>();
  for (const b of input.branches) push(branchesByIteration, b.iteration_id, b);

  const tasksByIteration = new Map<string, B2cTaskRow[]>();
  const unplannedByWorkstream = new Map<string, B2cTaskRow[]>();
  const tasksByWorkstream = new Map<string, B2cTaskRow[]>();
  for (const t of input.tasks) {
    push(tasksByWorkstream, t.workstream_key, t);
    if (t.iteration_id) push(tasksByIteration, t.iteration_id, t);
    else push(unplannedByWorkstream, t.workstream_key, t);
  }

  const iterationsByWorkstream = new Map<string, B2cIterationRow[]>();
  for (const i of input.iterations) push(iterationsByWorkstream, i.workstream_key, i);

  const mergedKeys = new Set(
    input.iterations.filter((i) => i.status === "merged").map((i) => i.workstream_key)
  );
  const byKey = new Map(input.workstreams.map((w) => [w.key, w]));
  const gatesByWorkstream = new Map<string, string[]>();
  for (const g of input.gates) push(gatesByWorkstream, g.workstream_key, g.builds_on_key);

  const viewOf = (w: B2cWorkstreamRow): WorkstreamView => {
    const sorted = [...(iterationsByWorkstream.get(w.key) ?? [])].sort(
      (a, b) => a.number - b.number
    );
    const iterations = sorted.map((iteration, index) => {
      const tasks = sortTasks(tasksByIteration.get(iteration.id) ?? []);
      const earlier = sorted.slice(0, index);
      const branches = sortBranches(branchesByIteration.get(iteration.id) ?? []);
      return {
        iteration,
        tasks,
        progress: progressOf(tasks),
        ...stacking(w.code, earlier, branchesByIteration),
        branches: branches.map((branch) => ({
          branch,
          base: repoBase(branch.repo, earlier, branchesByIteration),
        })),
        openRepos: branches.filter((b) => b.merged_at === null).map((b) => b.repo),
      };
    });
    const buildsOn = (gatesByWorkstream.get(w.key) ?? [])
      .map((key) => byKey.get(key))
      .filter((g): g is B2cWorkstreamRow => g !== undefined)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((g) => ({ key: g.key, code: g.code, title: g.title, merged: mergedKeys.has(g.key) }));
    const all = tasksByWorkstream.get(w.key) ?? [];
    return {
      workstream: w,
      iterations,
      unplanned: sortTasks(unplannedByWorkstream.get(w.key) ?? []),
      current: currentIteration(iterations),
      buildsOn,
      gateOpen: buildsOn.every((g) => g.merged),
      hasMerged: mergedKeys.has(w.key),
      progress: progressOf(all),
      e2eTask: all.find((t) => t.is_e2e_proof) ?? null,
    };
  };

  const views = input.workstreams.map(viewOf);
  const stageKeys = new Set(input.stages.map((s) => s.key));
  const stages = [...input.stages]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((stage) => {
      const workstreams = views.filter((v) => v.workstream.stage_key === stage.key);
      return { stage, workstreams, progress: sumProgress(workstreams.map((v) => v.progress)) };
    });
  const unstaged = views.filter(
    (v) => v.workstream.stage_key === null || !stageKeys.has(v.workstream.stage_key)
  );

  return {
    stages,
    unstaged,
    summary: {
      workstreams: views.length,
      marathons: input.iterations.filter((i) => i.status === "marathon").length,
      awaitingFeedback: input.iterations.filter((i) => i.status === "feedback").length,
      progress: sumProgress(views.map((v) => v.progress)),
    },
  };
}

/** Where an iteration's yango-app branch stands, given the iterations before it (ascending). */
export function stacking(
  workstreamCode: string,
  earlier: B2cIterationRow[],
  branchesByIteration: Map<string, B2cIterationBranchRow[]> = new Map()
): Pick<IterationView, "stackedOn" | "baseBranch" | "mergeBlockedBy"> {
  const previous = earlier[earlier.length - 1];
  const stackedOn =
    previous && previous.status !== "merged"
      ? {
          number: previous.number,
          branch:
            branchesByIteration.get(previous.id)?.find((b) => b.repo === "yango-app")?.branch ??
            defaultIntegrationBranch(workstreamCode, previous.number),
        }
      : null;
  const firstUnmerged = earlier.find((i) => i.status !== "merged");
  return {
    stackedOn,
    baseBranch: stackedOn?.branch ?? "main",
    mergeBlockedBy: firstUnmerged ? firstUnmerged.number : null,
  };
}

/**
 * A repo branch's base: the branch of the nearest earlier iteration that changed the same
 * repo, while that branch is unmerged; else main. Iterations that did not touch the repo
 * are skipped, so it3's branch stacks on it1's when it2 left the repo alone.
 */
export function repoBase(
  repo: B2cRepo,
  earlier: B2cIterationRow[],
  branchesByIteration: Map<string, B2cIterationBranchRow[]>
): string {
  for (let i = earlier.length - 1; i >= 0; i--) {
    const row = branchesByIteration.get(earlier[i]!.id)?.find((b) => b.repo === repo);
    if (row) return row.merged_at === null ? row.branch : "main";
  }
  return "main";
}

/**
 * Iterations start in order: the number of an earlier iteration of the same
 * workstream that is still `planned`, or null when none is. Starting a later
 * one first would invert the stack (its base branch would not exist yet).
 */
export function earlierUnstarted(iterations: B2cIterationRow[], number: number): number | null {
  const earlier = iterations
    .filter((i) => i.number < number && i.status === "planned")
    .sort((a, b) => a.number - b.number);
  return earlier[0]?.number ?? null;
}

/** A new iteration may be opened whenever none is still waiting to start. */
export function canOpenNextIteration(iterations: B2cIterationRow[]): boolean {
  return !iterations.some((i) => i.status === "planned");
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V) {
  const bucket = map.get(key);
  if (bucket) bucket.push(value);
  else map.set(key, [value]);
}

function sortBranches(branches: B2cIterationBranchRow[]): B2cIterationBranchRow[] {
  return [...branches].sort((a, b) => B2C_REPOS.indexOf(a.repo) - B2C_REPOS.indexOf(b.repo));
}

// The end-to-end proof always closes the list; the rest keep their board order.
function sortTasks(tasks: B2cTaskRow[]): B2cTaskRow[] {
  return [...tasks].sort(
    (a, b) => Number(a.is_e2e_proof) - Number(b.is_e2e_proof) || a.sort_order - b.sort_order
  );
}
